import React, { useState, useEffect, useContext, createContext, useCallback } from 'react';
import { FirebaseContext } from '../components/Firebase';
import { COPILOT_PLANS } from '../components/ai/copilot.config';

const defaultState = {
  settings: () => ('')
};
export const SettingsContext = createContext(defaultState);

export const SettingsProvider = (props) => {
  const { firebase, user } = useContext(FirebaseContext);
  const [settings, setSettings] = useState([]);
  const [isAiLocked, setIsAiLocked] = useState(false);
  const AI_MAX_CLICKS = 4;
  const AI_LOCK_TIME = 15 * 60 * 1000; // 15 Minuten in Millisekunden
  const CLICK_WINDOW = 5 * 60 * 1000; // 5 Minuten Zeitfenster für Klicks
  const [editableMode, setEditableMode] = useState(false);

  useEffect(() => {
    if (settings.length) {
      const editable = settings.some(item => item.editableMode);
      setEditableMode(editable);
    }
  }, [settings]);

  function toggleEditableMode() {
    setEditableMode(prev => !prev);
    // Falls du das Setting auch in der Datenbank speichern willst:
    // setSettings(prevSettings =>
    //   prevSettings.map(setting => ({
    //     ...setting,
    //     editableMode: !editableMode
    //   }))
    // );
  }


  useEffect(() => {
    // Sicherheitsüberprüfung
    if (!user?.organizationId || !firebase) {
      return;
    }
  
    let unsubscribe = null;
  
    try {
      // Listener für Organisationseinstellungen einrichten
      unsubscribe = firebase.getOrganizationSettings({
        organizationId: user.organizationId,
        handler: (snapshot) => {
          try {
            const userSettings = [];
            snapshot.forEach((doc) => {
              userSettings.push({
                id: doc.id,
                ...doc.data(),
              });
            });
            setSettings(userSettings);
            console.log("Settings loaded");
          } catch (error) {
            console.error("Error processing organization settings snapshot:", error);
          }
        },
      });
    } catch (error) {
      console.error("Error setting up organization settings listener:", error);
    }
  
    // Cleanup-Funktion
    return () => {
      if (typeof unsubscribe === "function") {
        try {
          unsubscribe();
        } catch (error) {
          console.error("Error during unsubscribe cleanup:", error);
        }
      }
    };
  }, [user?.organizationId, firebase]);
  

  // AI-Lock zurücksetzen
  const resetAiLock = useCallback(async (settingId) => {
    if (!user) return;

    console.log(settingId)

    await updateSettingsAiLock({
      settingId,
      aiClickHistory: [], // Leere Liste anstelle von Zählung
      aiLockTime: 0
    });

    setIsAiLocked(false);
  }, [user]);

  // Funktion zum Aktualisieren der AI-bezogenen Settings in Firebase
  const updateSettingsAiLock = async ({ settingId, aiClickHistory, aiLockTime = 0 }) => {
    try {
      await firebase.updateSettingsAiLock({
        settingId,
        aiClickHistory,
        aiLockTime,
      });
      console.log(`update setting entry ${settingId}`, aiClickHistory, aiLockTime);
    } catch (e) {
      console.log('An API error occurred', e);
    }
  };

  // Überwache den Lock-Zustand und setze ihn zurück, wenn die Zeit abgelaufen ist
  useEffect(() => {
    if (!user || settings.length === 0) return;

    const timeouts = settings.map(userSettings => {
      const aiLockTime = userSettings.aiLockTime || 0;
      const isCurrentlyLocked = Date.now() - aiLockTime < AI_LOCK_TIME;

      if (isCurrentlyLocked) {
        setIsAiLocked(true);

        // Setze Timeout, um den Lock zurückzusetzen, wenn die Lock-Zeit abgelaufen ist
        const timeoutId = setTimeout(() => {
          resetAiLock(userSettings.id);
        }, AI_LOCK_TIME - (Date.now() - aiLockTime));

        return timeoutId;
      } else {
        setIsAiLocked(false);
        return null;
      }
    });

    // Cleanup beim Unmount oder wenn settings geändert werden
    return () => timeouts.forEach(timeoutId => timeoutId && clearTimeout(timeoutId));
  }, [settings, AI_LOCK_TIME, user, resetAiLock]);

  // AI-Klickanzahl erhöhen und Lock setzen
  const incrementAiClick = useCallback(async (settingId) => {
    if (!user) return;

    const userSettings = settings.find(s => s.id === settingId) || {};
    const aiClickHistory = userSettings.aiClickHistory || [];
    const aiLockTime = userSettings.aiLockTime || 0;
    const isCurrentlyLocked = Date.now() - aiLockTime < AI_LOCK_TIME;

    if (isCurrentlyLocked) {
      console.log("AI is currently locked");
      return;
    }

    // Entferne Klicks, die außerhalb des CLICK_WINDOW liegen
    const now = Date.now();
    const recentClicks = aiClickHistory.filter(timestamp => now - timestamp < CLICK_WINDOW);

    // Aktualisiere die Klickhistorie
    recentClicks.push(now);

    if (recentClicks.length >= AI_MAX_CLICKS) {
      // Setze Lock, wenn maximale Klickanzahl innerhalb des Fensters erreicht ist
      await updateSettingsAiLock({
        settingId,
        aiClickHistory: recentClicks,
        aiLockTime: now // Setzt die Lockzeit, wenn AI gesperrt wird
      });
      setIsAiLocked(true);
    } else {
      // Aktualisiere Klickhistorie
      await updateSettingsAiLock({
        settingId,
        aiClickHistory: recentClicks
      });
    }

    // Lokalen Zustand aktualisieren
    setSettings(prevSettings =>
      prevSettings.map(setting =>
        setting.id === settingId
          ? { ...setting, aiClickHistory: recentClicks, aiLockTime: recentClicks.length >= AI_MAX_CLICKS ? now : aiLockTime }
          : setting
      )
    );

  }, [settings, user, AI_LOCK_TIME, CLICK_WINDOW]);


  
  const getCurrentCopilotUsage = useCallback((settingId, type = 'analysis') => {
    if (!settings.length) {
      return 0
    }

    const currentMonth = new Date().toISOString().slice(0, 7)

    const userSettings =
      settings.find(s => s.id === settingId) || settings[0]

    const usage = userSettings?.copilotUsage

    if (!usage || usage.month !== currentMonth) {
      return 0
    }

    return usage[type] || 0
  }, [settings])


const checkCopilotLimit = useCallback(
  (type = 'analysis', subscription) => {
    
//     console.log('=== COPILOT LIMIT DEBUG ===')
//     console.log('subscription:', subscription)
//     console.log('subscription.role:', subscription?.role)
//     console.log('requested type:', type)
//     console.log('available plans:', COPILOT_PLANS)
    
    if (!subscription) {
      return false
    }

    const role = subscription.role

    if (!role) {
      return false
    }

    const plan = COPILOT_PLANS[role]

    if (!plan?.enabled) {
      return false
    }

    const limit = plan.monthlyLimits?.[type]

    if (typeof limit !== 'number') {
      return false
    }

    const currentMonth = new Date().toISOString().slice(0, 7)

    const userSettings = settings[0]
    const usage = userSettings?.copilotUsage

    const currentUsage =
      usage?.month === currentMonth
        ? usage[type] || 0
        : 0

    return currentUsage < limit
  },
  [settings]
)




const incrementCopilotUsage = useCallback(async (type = 'analysis') => {
  if (!user || !firebase) {
    console.log('Copilot usage: missing user or firebase')
    return
  }

  if (!['analysis', 'actions'].includes(type)) {
    console.error('Invalid Copilot usage type:', type)
    return
  }

  const userSettings = settings[0]

  if (!userSettings?.id) {
    console.log('Copilot usage: no settings document found')
    return
  }

  const currentMonth = new Date()
    .toISOString()
    .slice(0, 7)

  const currentUsage = userSettings.copilotUsage || {}

  const isSameMonth =
    currentUsage.month === currentMonth

  const analysisCount =
    isSameMonth
      ? currentUsage.analysis || 0
      : 0

  const actionsCount =
    isSameMonth
      ? currentUsage.actions || 0
      : 0

  const nextUsage = {
    month: currentMonth,
    analysis: analysisCount,
    actions: actionsCount,
  }

  nextUsage[type] += 1

  console.log('Increment Copilot usage:', {
    settingId: userSettings.id,
    type,
    usage: nextUsage,
  })

  try {
    await firebase.updateCopilotUsage({
      settingId: userSettings.id,
      copilotUsage: nextUsage,
    })

    setSettings(prevSettings =>
      prevSettings.map(setting =>
        setting.id === userSettings.id
          ? {
              ...setting,
              copilotUsage: nextUsage,
            }
          : setting
      )
    )
  } catch (error) {
    console.error(
      'Error updating Copilot usage:',
      error
    )

    throw error
  }
}, [firebase, user, settings])



  return (
    <SettingsContext.Provider
      value={{
        settings,
        setSettings,
        editableMode,
        toggleEditableMode,
        incrementAiClick,
        resetAiLock,
        isAiLocked,
        getCurrentCopilotUsage,
        incrementCopilotUsage,
        checkCopilotLimit,
      }}
    >
      {props.children}
    </SettingsContext.Provider>
  );
};

// Custom Hook für den Zugriff auf Settings
export const useSettings = () => useContext(SettingsContext);
